import React, { useMemo, useRef, useEffect, useState } from 'react';
import PlanCard from './PlanCard';

const PlanGrid = ({ plans, onPlanSelect, loading, error }) => {
  // Filter valid plans
  const validPlans = useMemo(() => {
    if (!plans || plans.length === 0) return [];
    return plans.filter((plan) => plan && plan.name && plan.planCode);
  }, [plans]);

  const scrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  // Setup fade visibility + auto "nudge" scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateFades = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setShowLeftFade(scrollLeft > 5);
      setShowRightFade(scrollLeft + clientWidth < scrollWidth - 5);
    };

    // Check if scrolling is actually possible
    if (el.scrollWidth > el.clientWidth + 5) {
      setShowRightFade(true);

      // Auto nudge: small scroll right then back
      el.scrollTo({ left: 30, behavior: 'smooth' });
      const timeout = setTimeout(() => {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      }, 400);

      // Cleanup
      return () => clearTimeout(timeout);
    } else {
      setShowLeftFade(false);
      setShowRightFade(false);
    }

    updateFades();
  }, [validPlans.length]); // rerun when plan count changes

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftFade(scrollLeft > 5);
    setShowRightFade(scrollLeft + clientWidth < scrollWidth - 5);
  };

  // Loading state
  if (loading) {
    return (
      <div>
        <p className="text-xs text-gray-500 mb-2 text-right">
          Scroll to see all plans →
        </p>
        <div className="relative">
          {/* Fades (optional while loading) */}
          <div className="pointer-events-none absolute top-0 right-0 w-10 h-full bg-gradient-to-l from-white to-transparent z-10" />
          <div className="flex gap-6 flex-nowrap overflow-x-auto pb-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-[280px] bg-white rounded-2xl p-5 border border-gray-200 shadow-sm animate-pulse flex-shrink-0"
              >
                <div className="h-6 bg-gray-200 rounded mb-3"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-8 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-4xl mb-4">⚠️</div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          Failed to load plans
        </h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty / no valid plans
  if (!validPlans || validPlans.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-4xl mb-4">📋</div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          No plans available
        </h3>
        <p className="text-gray-600">
          Please check back later or contact support.
        </p>
      </div>
    );
  }

  // Main horizontal scroll row
  return (
    <div>
      <p className="text-xs text-gray-500 mb-2 text-right">
        Scroll to see all plans →
      </p>

      <div className="relative">
        {/* Left fade */}
        {showLeftFade && (
          <div className="pointer-events-none absolute top-0 left-0 w-10 h-full bg-gradient-to-r from-white to-transparent z-10" />
        )}

        {/* Right fade */}
        {showRightFade && (
          <div className="pointer-events-none absolute top-0 right-0 w-10 h-full bg-gradient-to-l from-white to-transparent z-10" />
        )}

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-6 flex-nowrap overflow-x-auto pb-3"
        >
          {validPlans.map((plan) => (
            <div key={plan.planCode} className="w-[280px] flex-shrink-0">
              <PlanCard
                plan={plan}
                onSelect={() => onPlanSelect(plan)}
                isPopular={plan.name === 'Elite'}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlanGrid;
